import './App.css';
import { useEffect, useState, lazy, Suspense } from 'react';
import {
    BrowserRouter as Router,
    Routes,
    Route,
} from "react-router-dom";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { ErrorBoundary } from 'react-error-boundary';
import { SubscriptionStatusContext } from './common/Contexts/Contexts';

import { default as WwwHome } from './www/Home';
// import Blog from './www/Blog';
// import BlogPost from './www/BlogPost';
// import Tutorials from './www/Tutorials';
// import Support from './deepkpi/Support';
// import AppView from './deepkpi/appview/AppView';
// import ChatView from './deepanalysis/ChatView';
// import { default as DeepKpiHome } from './deepkpi/Home';
// import { default as DeepAnalysisHome } from './deepanalysis/Home';
// import { default as DeepInsightHome } from './deepinsight/Home';

import { ProvenanceViewer } from './deepkpi/appview/ProvenanceViewer';
// import UpdateFeed from './deepkpi/appview/UpdateFeed';

import { Amplify } from '@aws-amplify/core';
import { Authenticator } from '@aws-amplify/ui-react';
// import FreeAppView from './deepkpi/appview/FreeAppView';
// import FreeMobileAppView from './deepkpi/appview/FreeMobileAppView';
// import MobileAppView from './deepkpi/appview/MobileAppView';
// import ShareChartView from './deepkpi/appview/ShareChartView';

import {
    ampli,
    getTheme,
    AppContainer,
    ErrorBox,
    TopAppBar,
    AUTHN_CONFIG,
    // RequireAuthN,
    // RequireSubscription,
    // Login,
    // Logout,
    // ChangePassword,
    // UserProfile,
    // Subscription,
    // Dashboard,
    // Coupon
} from './common';

import { useIsMobile } from './common/Utils';

const Blog = lazy(() => import('./www/Blog'));
const BlogPost = lazy(() => import('./www/BlogPost'));
const Tutorials = lazy(() => import('./www/Tutorials'));
const Support = lazy(() => import('./deepkpi/Support'));
const AppView = lazy(() => import('./deepkpi/appview/AppView'));
const ChatView = lazy(() => import('./deepanalysis/ChatView'));
const DeepKpiHome = lazy(() => import('./deepkpi/Home'));
const DeepAnalysisHome = lazy(() => import('./deepanalysis/Home'));
const DeepInsightHome = lazy(() => import('./deepinsight/Home'));
const UpdateFeed = lazy(() => import('./deepkpi/appview/UpdateFeed'));
const DeepInsightCatalog = lazy(() => import('./deepinsight/Catalog'));
const PricingPage = lazy(() => import('./common/Subscription/Pricing'));

const FreeAppView = lazy(() => import('./deepkpi/appview/FreeAppView'));
const FreeMobileAppView = lazy(() => import('./deepkpi/appview/FreeMobileAppView'));
const MobileAppView = lazy(() => import('./deepkpi/appview/MobileAppView'));
const ShareChartView = lazy(() => import('./deepkpi/appview/ShareChartView'));

const RequireAuthN = lazy(() => import('./common/RequireAuth').then(module => ({ default: module.RequireAuthN })));
const RequireSubscription = lazy(() => import('./common/RequireAuth').then(module => ({ default: module.RequireSubscription })));
const Login = lazy(() => import('./common/Login').then(module => ({ default: module.Login })));
const SignUp = lazy(() => import('./common/Login/SignUp').then(module => ({ default: module.SignUp })));
const Inquiry = lazy(() => import('./common/Subscription/Inquiry').then(module => ({ default: module.Inquiry })));
const Logout = lazy(() => import('./common/Login').then(module => ({ default: module.Logout })));
const ChangePassword = lazy(() => import('./common/ChangePassword').then(module => ({ default: module.ChangePassword })));
const UserProfile = lazy(() => import('./common/UserProfile').then(module => ({ default: module.UserProfile })));
const Subscription = lazy(() => import('./common/Subscription').then(module => ({ default: module.Subscription })));
const Dashboard = lazy(() => import('./common/Dashboard').then(module => ({ default: module.Dashboard })));
const Launchpad = lazy(() => import('./common/Launchpad/Launchpad'));
const Coupon = lazy(() => import('./common/Coupon').then(module => ({ default: module.Coupon })));

Amplify.configure(AUTHN_CONFIG);

export function App() {
    document.title = 'Revelata | Fundamental equities analysis made easy'
    const Theme = createTheme(getTheme('dark'));

    // Global subscription status state
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);

    // Detect if an OAuth error was passed in as a URL parameter:
    const isReturnedOAuthError =
        window.location.pathname === '/dashboard' &&
        window.initialUrlParams &&
        (window.initialUrlParams.has('error') || window.initialUrlParams.has('error_description'));

    const errorInfo = isReturnedOAuthError ? {
        error: window.initialUrlParams.get('error'),
        errorDescription: window.initialUrlParams.get('error_description'),
        state: window.initialUrlParams.get('state')
    } : null;
    console.log(errorInfo, isReturnedOAuthError, window.location.pathname, window.initialUrlParams);

    useEffect(() => {
        return () => { ampli.flush(); };  // This is called when the component is unmounted.
    }, []);

    const isMobile = useIsMobile();
    
    return (

        <ThemeProvider theme={Theme}>
            <Authenticator.Provider>
                <Router>
                    {['/free', '/share', '/mobile'].includes(window.location.pathname) ?
                        <Routes>
                            <Route path="/free" element={(isMobile) ? <FreeMobileAppView /> : <FreeAppView />} />
                            <Route path="/mobile" element={<FreeMobileAppView />} />
                            <Route path="/share" element={<ShareChartView />} />
                        </Routes>
                        :
                        <>
                        <SubscriptionStatusContext.Provider value={[subscriptionStatus, setSubscriptionStatus]}>
                            <TopAppBar/>
                                <ErrorBoundary FallbackComponent={ErrorBox}>
                                <Suspense fallback={null}>
                                    <Routes>
                                        <Route path="/" element={<AppContainer noSidebar><WwwHome /></AppContainer>} />
                                        <Route path="/blog" element={<AppContainer noSidebar><Blog /></AppContainer>} />
                                        <Route path="/blog/:slug" element={<AppContainer noSidebar><BlogPost /></AppContainer>} />
                                        <Route path="/support" element={<AppContainer noSidebar><Support /></AppContainer>} />
                                        <Route path="/deepkpi/support" element={<AppContainer noSidebar><Support /></AppContainer>} />
                                        <Route path="/tutorials" element={<AppContainer noSidebar><Tutorials /></AppContainer>} />
                                        <Route path="/login" element={<AppContainer noSidebar><Login key="login" tab="signIn" /></AppContainer>} />
                                        <Route path="/logout" element={<AppContainer noSidebar><Logout /></AppContainer>} />
                                        <Route path="/signup" element={<AppContainer noSidebar><SignUp key="signup" tab="signUp" /></AppContainer>} />
                                        <Route path="/inquiry" element={<AppContainer noSidebar><Inquiry /></AppContainer>} />
                                        <Route path="/pricing" element={<AppContainer noSidebar><PricingPage /></AppContainer>} />
                                        <Route path="/docviewer" element={<AppContainer noSidebar><ProvenanceViewer /></AppContainer>} />
                                        <Route path="/deepkpi" element={<AppContainer noSidebar><DeepKpiHome /></AppContainer>} />
                                        <Route path="/deepanalysis" element={<AppContainer noSidebar><DeepAnalysisHome /></AppContainer>} />
                                        <Route path="/deepinsight" element={<AppContainer noSidebar><DeepInsightHome /></AppContainer>} />

                                        <Route path="/deepkpi/app" element={<AppContainer><RequireAuthN onFailureRoute="/deepkpi"><RequireSubscription group="sg"> {(isMobile) ? <MobileAppView incomingSelectedPane="kpis" /> : <AppView />}</RequireSubscription></RequireAuthN></AppContainer>} />
                                        <Route path="/deepanalysis/app" element={<AppContainer><RequireAuthN onFailureRoute="/deepanalysis"><RequireSubscription group="sg">{(isMobile) ? <MobileAppView incomingSelectedPane="ai" /> : <ChatView />}</RequireSubscription></RequireAuthN></AppContainer>} />
                                        <Route path="/deepkpi/newsfeed" element={<AppContainer><RequireAuthN onFailureRoute="/"><RequireSubscription group="sg">{(isMobile) ? <MobileAppView incomingSelectedPane="newsfeed" /> : <UpdateFeed />}</RequireSubscription></RequireAuthN></AppContainer>} />
                                        <Route path="/subscribe" element={<AppContainer><RequireAuthN><Subscription /></RequireAuthN></AppContainer>} />
                                        <Route path="/dashboard" element={<Dashboard errorInfo={errorInfo} isReturnedOAuthError={isReturnedOAuthError} />} />
                                        <Route path="/launchpad" element={<AppContainer><RequireAuthN><RequireSubscription group="sg"><Launchpad /></RequireSubscription></RequireAuthN></AppContainer>} />
                                        <Route path="/changepassword" element={<AppContainer><RequireAuthN><ChangePassword /></RequireAuthN></AppContainer>} />
                                        <Route path="/profile" element={<AppContainer><RequireAuthN><UserProfile /></RequireAuthN></AppContainer>} />
                                        <Route path="/coupon" element={<AppContainer><RequireAuthN><RequireSubscription group="sg"><Coupon /></RequireSubscription></RequireAuthN></AppContainer>} />

                                        <Route path="*" element={<AppContainer noSidebar><WwwHome /></AppContainer>} />
                                    </Routes>
                                </Suspense>
                                </ErrorBoundary>
                        </SubscriptionStatusContext.Provider>
                        </>
                    }
                </Router>
            </Authenticator.Provider>
        </ThemeProvider>

    );
}

export default App;